import { useSelector } from '@/api/state';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { Button, Modal } from '@payaca/ambrosia';
import { hasUnlimitedUsers } from '@payaca/helpers/subscriptionHelper';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { User } from '@payaca/types/userTypes';
import { FC, useMemo, type JSX } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import AddUserForm from '../addUserForm/AddUserForm';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAddUserSuccess: () => void;
};
const AddUserModal: FC<Props> = ({
  isOpen,
  onClose,
  onAddUserSuccess,
}: Props): JSX.Element => {
  const history = useHistory();
  const { show } = useIntercom();
  const numberOfUserSeats = useSelector((state) => {
    return (
      state.account?.accountAccessInformation?.features?.numberOfUserSeats ?? 0
    );
  });
  const hasActiveTrialPeriod = useSelector((state) => {
    const accountAccessInformation = state.account?.accountAccessInformation;
    if (!accountAccessInformation) return false;

    return (
      !accountAccessInformation.hasActiveSubscription &&
      !accountAccessInformation.isTrialPeriodExpired
    );
  });

  const numberOfActiveUsers = useSelector((state: any) => {
    return state.users.accountUsers.filter(
      (accountUser: User) =>
        !accountUser.deactivatedAt || accountUser.inviteToken
    ).length;
  });

  const canAddNewUser = useMemo(() => {
    return hasActiveTrialPeriod || numberOfUserSeats > numberOfActiveUsers;
  }, [numberOfUserSeats, numberOfActiveUsers, hasActiveTrialPeriod]);

  const seatsInfo = !hasActiveTrialPeriod ? (
    <p className="mb-4 text-gray-500">
      Currently using{' '}
      <strong>
        {numberOfActiveUsers} out of{' '}
        {hasUnlimitedUsers(numberOfUserSeats) ? 'unlimited' : numberOfUserSeats}
      </strong>{' '}
      available user spaces.
    </p>
  ) : undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add user" size="sm">
      {isOpen &&
        (canAddNewUser ? (
          <AddUserForm
            seatsInfo={seatsInfo}
            onAddUserSuccess={onAddUserSuccess}
            onClose={onClose}
          />
        ) : (
          <>
            <Modal.Body>
              {seatsInfo}
              <p>
                All the user spaces associated with your account have been
                filled.
              </p>
              <p>
                If you wish to add additional users, please upgrade your
                subscription plan or{' '}
                <button
                  type="button"
                  className="cursor-pointer appearance-none border-0 bg-transparent p-0 font-semibold text-blue-600 underline"
                  onClick={show}
                >
                  contact us directly
                </button>
                .
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Modal.Footer.Actions>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
                <PermissionGuard
                  renderIfHasPermissions={[
                    AccountsPermissions.UPDATE_SUBSCRIPTION,
                  ]}
                >
                  <Button onClick={() => history.push('/upgradeAccount')}>
                    Manage subscription
                  </Button>
                </PermissionGuard>
              </Modal.Footer.Actions>
            </Modal.Footer>
          </>
        ))}
    </Modal>
  );
};

export default AddUserModal;
